import { __importDefault, __importStar } from "tslib";
import { CertNaming } from "@ndn/keychain";
import { Name } from "@ndn/packet";
import _cjsDefaultImport0 from "obliterator/some.js"; const some = __importDefault(_cjsDefaultImport0).default;
export var Vars;
(function (Vars) {
    /** Check if lhs and rhs are consistent, i.e. have no key with different values. */
    function consistent(lhs, rhs) {
        for (const [k, lv] of lhs) {
            const rv = rhs.get(k);
            if (rv && !lv.equals(rv)) {
                return false;
            }
        }
        return true;
    }
    Vars.consistent = consistent;
})(Vars || (Vars = {}));
const emptyVars = new Map();
export var VarsLike;
(function (VarsLike) {
    /** Convert VarsLike to an iterable that may be passed to Map constructor to create Vars. */
    function toIterable(vars) {
        return vars instanceof Map ? vars : Object.entries(vars);
    }
    VarsLike.toIterable = toIterable;
})(VarsLike || (VarsLike = {}));
/** Context of matching a name. */
class MatchState {
    name;
    pos;
    vars;
    /**
     * Constructor.
     * @param name - Input name.
     * @param pos - Position of first unconsumed component.
     * @param vars - Recognized variables.
     */
    constructor(name, pos = 0, vars = emptyVars) {
        this.name = name;
        this.pos = pos;
        this.vars = vars;
    }
    /** Length of unconsumed name. */
    get tailLength() { return this.name.length - this.pos; }
    /**
     * Get first i components of unconsumed name.
     * @param i - Number of components, must be non-negative.
     */
    tail(i = Infinity) {
        return this.name.slice(this.pos, this.pos + i);
    }
    /** Whether the input name has been accepted by pattern. */
    get accepted() {
        return this.tailLength === 0;
    }
    extend(incrementPos, ...varsL) {
        const result = new Map(this.vars);
        for (const vars of varsL) {
            for (const [k, rv] of vars) {
                const lv = result.get(k);
                if (lv?.equals(rv) === false) {
                    return false;
                }
                result.set(k, rv);
            }
        }
        return new MatchState(this.name, this.pos + incrementPos, result);
    }
    /* v8 ignore start: only used for debug */
    toString() {
        return `MatchState(${this.name.getPrefix(this.pos)}; ${this.tail()}; ${Array.from(this.vars, ([k, v]) => `${k}=${v}`).join(", ")})`;
    }
}
/** Context of constructing a name. */
class BuildState {
    name;
    vars;
    constructor(name, vars) {
        this.name = name;
        this.vars = vars;
    }
    append(...comps) {
        return new BuildState(this.name.append(...comps), this.vars);
    }
    /* v8 ignore start: only used for debug */
    toString() {
        return `BuildState(${this.name}; ${Array.from(this.vars, ([k, v]) => `${k}=${v}`).join(", ")})`;
    }
}
/** Structure of a name. */
export class Pattern {
    /**
     * Determine whether a name matches the structure of this pattern.
     * @param name - Input name.
     * @returns - Iterable of extracted fields in possible interpretations.
     */
    *match(name) {
        const [min, max] = this.matchLengthRange;
        if (!(name.length >= min && name.length <= max)) {
            return;
        }
        const initial = new MatchState(name);
        for (const final of this.matchState(initial)) {
            if (final.accepted) {
                yield final.vars;
            }
        }
    }
    static matchState(p, state) {
        return p.matchState(state);
    }
    /**
     * Determine minimum and maximum match length.
     *
     * @remarks
     * This optimization is used in {@link match} for early rejection of input names that are
     * either too short or too long.
     */
    get matchLengthRange() {
        return (this.matchLengthRange_ ??= this.computeMatchLengthRange());
    }
    matchLengthRange_;
    /**
     * Build names following the structure of this pattern.
     * @param varsL - Sets of variables to be replaced into the name.
     * @returns Iterable of possible names.
     */
    *build(...varsL) {
        const varsM = new Map();
        for (const vars of varsL) {
            for (const [k, v] of VarsLike.toIterable(vars)) {
                varsM.set(k, Name.from(v));
            }
        }
        const initial = new BuildState(new Name(), varsM);
        for (const final of this.buildState(initial)) {
            yield final.name;
        }
    }
    /**
     * Build part of an output name.
     * @returns Iterable of potential constructions.
     */
    static buildState(p, state) {
        return p.buildState(state);
    }
}
/** Match or construct a constant name portion. */
export class ConstPattern extends Pattern {
    constructor(name) {
        super();
        this.name = Name.from(name);
    }
    name;
    *matchState(state) {
        if (this.name.length <= state.tailLength &&
            this.name.comps.every((comp, i) => comp.equals(state.name.get(state.pos + i)))) {
            yield state.extend(this.name.length);
        }
    }
    computeMatchLengthRange() {
        return [this.name.length, this.name.length];
    }
    *buildState(state) {
        yield state.append(...this.name.comps);
    }
}
/**
 * Match or construct a variable name portion.
 *
 * @remarks
 * When matching a name, this pattern extracts a number of name components, and saves the sub-name
 * in variables object in {@link VariablePattern.match} return value.
 *
 * When building a name, this pattern succeeds if the variable is present in
 * {@link VariablePattern.build} function argument.
 */
export class VariablePattern extends Pattern {
    id;
    /**
     * Constructor
     * @param id - Variable name.
     */
    constructor(id, { minComps = 1, maxComps = 1, inner, filter, } = {}) {
        super();
        this.id = id;
        this.minComps = minComps;
        this.maxComps = maxComps;
        this.inner = inner;
        this.filter = filter;
    }
    minComps;
    maxComps;
    inner;
    filter;
    *innerMatch(value, input) {
        if (!this.inner) {
            yield emptyVars;
            return;
        }
        for (const m of this.inner.match(value)) {
            if (!input || Vars.consistent(input, m)) {
                yield m;
            }
        }
    }
    filtersAccept(value, vars) {
        return !this.filter || this.filter.accept(value, vars);
    }
    *matchState(state) {
        for (let i = this.minComps, max = Math.min(state.tailLength, this.maxComps); i <= max; ++i) {
            const value = state.tail(i);
            for (const innerVars of this.innerMatch(value)) {
                const s = state.extend(i, innerVars, [[this.id, value]]);
                if (s && this.filtersAccept(value, s.vars)) {
                    yield s;
                }
            }
        }
    }
    computeMatchLengthRange() {
        return [this.minComps, this.maxComps];
    }
    *buildState(state) {
        const value = state.vars.get(this.id);
        if (value) {
            if (value.length < this.minComps || value.length > this.maxComps) {
                return;
            }
            let hasMatch = false;
            for (const innerVars of this.innerMatch(value, state.vars)) {
                if (this.filtersAccept(value, innerVars)) {
                    hasMatch = true;
                    break;
                }
            }
            if (hasMatch) {
                yield state.append(...value.comps);
            }
            return;
        }
        if (!this.inner) {
            return;
        }
        for (const b of Pattern.buildState(this.inner, state)) {
            if (this.filtersAccept(b.name.slice(state.name.length), b.vars)) {
                yield b;
            }
        }
    }
}
(function (VariablePattern) {
    /** Create a filter that accepts a name component if it satisfies a convention. */
    class ConventionFilter {
        convention;
        constructor(convention) {
            this.convention = convention;
        }
        accept(name) {
            return name.length === 1 && name.get(0).is(this.convention);
        }
    }
    VariablePattern.ConventionFilter = ConventionFilter;
})(VariablePattern || (VariablePattern = {}));
/* eslint-disable tsdoc/syntax -- tsdoc-missing-reference */
/**
 * Match or construct a KeyLocator or certificate name.
 *
 * @remarks
 * To match a KeyLocator or certificate name, use a {@link ConcatPattern} that contains
 * patterns to match the subject name, followed by a {@link CertNamePattern} at last.
 * The captured variable contains the whole KeyLocator or certificate name that can
 * be further recognized by {@link CertNaming.parseKeyName} and {@link CertNaming.parseCertName}.
 *
 * Using the same {@link ConcatPattern}, the constructed name would be the subject name only.
 * It can be passed to {@link \@ndn/keychain!KeyChain.getSigner} to find a key/certificate.
 */
/* eslint-enable tsdoc/syntax */
export class CertNamePattern extends Pattern {
    *matchState(state) {
        if ([2, 4].includes(state.tailLength) &&
            state.name.get(state.pos).equals(CertNaming.KEY)) {
            yield state.extend(state.tailLength);
        }
    }
    computeMatchLengthRange() {
        return [2, 4];
    }
    *buildState(state) {
        yield state;
    }
}
/** Concatenate several patterns. */
export class ConcatPattern extends Pattern {
    parts;
    constructor(parts) {
        super();
        this.parts = parts;
    }
    *matchState(state, partIndex = 0) {
        if (partIndex >= this.parts.length) {
            yield state;
            return;
        }
        const part = this.parts[partIndex];
        for (const partial of Pattern.matchState(part, state)) {
            yield* this.matchState(partial, partIndex + 1);
        }
    }
    computeMatchLengthRange() {
        return this.parts.reduce(([minSum, maxSum], p) => {
            const [min, max] = p.matchLengthRange;
            return [minSum + min, maxSum + max];
        }, [0, 0]);
    }
    *buildState(state, partIndex = 0) {
        if (partIndex >= this.parts.length) {
            yield state;
            return;
        }
        const part = this.parts[partIndex];
        for (const partial of Pattern.buildState(part, state)) {
            yield* this.buildState(partial, partIndex + 1);
        }
    }
}
/** Specify several alternate patterns in "OR" relation. */
export class AlternatePattern extends Pattern {
    choices;
    constructor(choices) {
        super();
        this.choices = choices;
    }
    *matchState(state) {
        for (const choice of this.choices) {
            yield* Pattern.matchState(choice, state);
        }
    }
    computeMatchLengthRange() {
        return this.choices.reduce(([minMin, maxMax], p) => {
            const [min, max] = p.matchLengthRange;
            return [Math.min(minMin, min), Math.max(maxMax, max)];
        }, [Infinity, 0]);
    }
    *buildState(state) {
        for (const choice of this.choices) {
            yield* Pattern.buildState(choice, state);
        }
    }
}
/**
 * Specify several overlapped patterns in "AND" relation.
 *
 * @remarks
 * When matching a name, every branch of this pattern must extract the same number of name
 * components, and their variables must be consistent.
 *
 * When building a name, one branch is used to build the name as long as all required variables
 * are present, and then the built name must match all branches.
 */
export class OverlapPattern extends Pattern {
    branches;
    constructor(branches) {
        super();
        this.branches = branches;
    }
    *matchState(state, branchIndex = 0, lastMatch) {
        if (branchIndex >= this.branches.length) {
            if (lastMatch) {
                yield lastMatch;
            }
            return;
        }
        const branch = this.branches[branchIndex];
        for (const submatch of Pattern.matchState(branch, state)) {
            if (branchIndex > 0 && lastMatch.pos !== submatch.pos) {
                continue;
            }
            yield* this.matchState(submatch.extend(state.pos - submatch.pos), branchIndex + 1, submatch);
        }
    }
    computeMatchLengthRange() {
        return this.branches.reduce(([minMax, maxMin], p) => {
            const [min, max] = p.matchLengthRange;
            return [Math.max(minMax, min), Math.min(maxMin, max)];
        }, [0, Infinity]);
    }
    *buildState(state) {
        for (const branch of this.branches) {
            for (const built of Pattern.buildState(branch, state)) {
                const ms = new MatchState(built.name, state.name.length, built.vars);
                if (some(this.matchState(ms), (rematch) => rematch.accepted)) {
                    yield built;
                }
            }
        }
    }
}
